import _ from 'lodash';
import { adminClient as apolloClient, safeClearStore } from '@/store/apollo';

import BoardGql from '@/extensions/tableReservation/store/graphql/queries/Board.gql';
import ReservationGql from '@/extensions/tableReservation/store/graphql/queries/Reservation.gql';
import CreateReservationGql from '@/extensions/tableReservation/store/graphql/mutations/CreateReservation.gql';
import UpdateReservationGql from '@/extensions/tableReservation/store/graphql/mutations/UpdateReservation.gql';
import DeleteReservationGql from '@/extensions/tableReservation/store/graphql/mutations/DeleteReservation.gql';
import CreateRoomGql from '@/extensions/tableReservation/store/graphql/mutations/CreateRoom.gql';
import UpdateRoomGql from '@/extensions/tableReservation/store/graphql/mutations/UpdateRoom.gql';
import DeleteRoomGql from '@/extensions/tableReservation/store/graphql/mutations/DeleteRoom.gql';
import CreateTableGql from '@/extensions/tableReservation/store/graphql/mutations/CreateTable.gql';
import UpdateTableGql from '@/extensions/tableReservation/store/graphql/mutations/UpdateTable.gql';
import DeleteTableGql from '@/extensions/tableReservation/store/graphql/mutations/DeleteTable.gql';
import CreateFixtureGql from '@/extensions/tableReservation/store/graphql/mutations/CreateFixture.gql';
import UpdateFixtureGql from '@/extensions/tableReservation/store/graphql/mutations/UpdateFixture.gql';
import DeleteFixtureGql from '@/extensions/tableReservation/store/graphql/mutations/DeleteFixture.gql';
import UpdateSettingsGql from '@/extensions/tableReservation/store/graphql/mutations/UpdateSettings.gql';
import CreateTableCombinationGql from '@/extensions/tableReservation/store/graphql/mutations/CreateTableCombination.gql';
import UpdateTableCombinationGql from '@/extensions/tableReservation/store/graphql/mutations/UpdateTableCombination.gql';
import DeleteTableCombinationGql from '@/extensions/tableReservation/store/graphql/mutations/DeleteTableCombination.gql';
import { normalizeApiDate } from '@/extensions/tableReservation/store/utils';

function normalizeError(error) {
    const message = _.get(error, 'graphQLErrors.0.message')
        || _.get(error, 'networkError.result.message')
        || _.get(error, 'networkError.message')
        || _.get(error, 'message')
        || 'Unexpected error';

    return new Error(message);
}

function sanitizePayload(payload) {
    return _.removeDeepByKey(_.cloneDeep(payload), '__typename');
}

async function runMutation(mutation, variables, field) {
    try {
        const response = await apolloClient.mutate({
            mutation: mutation,
            variables: variables
        });

        await safeClearStore(apolloClient);
        return _.get(response, 'data.' + field);
    } catch (error) {
        throw normalizeError(error);
    }
}

export default {
    async fetchBoard(cmsId, date) {
        if (cmsId === undefined || date === undefined) {
            throw new Error('required argument is missing');
        }

        const normalizedDate = normalizeApiDate(date);
        if (!normalizedDate) {
            throw new Error('Invalid date');
        }

        try {
            const response = await apolloClient.query({
                query: BoardGql,
                variables: {
                    cms: parseInt(cmsId, 10),
                    date: normalizedDate
                },
                fetchPolicy: 'network-only'
            });

            return _.get(response, 'data.EX_TRSV_Board');
        } catch (error) {
            throw normalizeError(error);
        }
    },

    async fetchReservation(id) {
        if (id === undefined) {
            throw new Error('required argument is missing');
        }

        try {
            const response = await apolloClient.query({
                query: ReservationGql,
                variables: {
                    id: parseInt(id, 10)
                },
                fetchPolicy: 'network-only'
            });

            return _.get(response, 'data.EX_TRSV_Reservation');
        } catch (error) {
            throw normalizeError(error);
        }
    },

    createReservation(reservation) {
        if (reservation === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(CreateReservationGql, { reservation: sanitizePayload(reservation) }, 'EX_TRSV_CreateReservation');
    },

    updateReservation(reservation) {
        if (reservation === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(UpdateReservationGql, { reservation: sanitizePayload(reservation) }, 'EX_TRSV_UpdateReservation');
    },

    deleteReservation(id) {
        if (id === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(DeleteReservationGql, { id: parseInt(id, 10) }, 'EX_TRSV_DeleteReservation');
    },

    createRoom(room) {
        if (room === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(CreateRoomGql, { room: sanitizePayload(room) }, 'EX_TRSV_CreateRoom');
    },

    updateRoom(room) {
        if (room === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(UpdateRoomGql, { room: sanitizePayload(room) }, 'EX_TRSV_UpdateRoom');
    },

    deleteRoom(id) {
        if (id === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(DeleteRoomGql, { id: parseInt(id, 10) }, 'EX_TRSV_DeleteRoom');
    },

    createTable(table) {
        if (table === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(CreateTableGql, { table: sanitizePayload(table) }, 'EX_TRSV_CreateTable');
    },

    updateTable(table) {
        if (table === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(UpdateTableGql, { table: sanitizePayload(table) }, 'EX_TRSV_UpdateTable');
    },

    deleteTable(id) {
        if (id === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(DeleteTableGql, { id: parseInt(id, 10) }, 'EX_TRSV_DeleteTable');
    },

    createFixture(fixture) {
        if (fixture === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(CreateFixtureGql, { fixture: sanitizePayload(fixture) }, 'EX_TRSV_CreateFixture');
    },

    updateFixture(fixture) {
        if (fixture === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(UpdateFixtureGql, { fixture: sanitizePayload(fixture) }, 'EX_TRSV_UpdateFixture');
    },

    deleteFixture(id) {
        if (id === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(DeleteFixtureGql, { id: parseInt(id, 10) }, 'EX_TRSV_DeleteFixture');
    },

    updateSettings(settings) {
        if (settings === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(UpdateSettingsGql, { settings: sanitizePayload(settings) }, 'EX_TRSV_UpdateSettings');
    },

    createTableCombination(combination) {
        if (combination === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(CreateTableCombinationGql, { combination: sanitizePayload(combination) }, 'EX_TRSV_CreateTableCombination');
    },

    updateTableCombination(combination) {
        if (combination === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(UpdateTableCombinationGql, { combination: sanitizePayload(combination) }, 'EX_TRSV_UpdateTableCombination');
    },

    deleteTableCombination(id) {
        if (id === undefined) {
            throw new Error('required argument is missing');
        }

        return runMutation(DeleteTableCombinationGql, { id: parseInt(id, 10) }, 'EX_TRSV_DeleteTableCombination');
    }
};
